/**
 * MacroManager - Executes campaign macros (onLaunch, onJoin, onExit, onInactive)
 *
 * Responsibilities:
 * - Execute onLaunchMacro when game launches (awaited before matchmakeable)
 * - Execute onJoinMacro when match found (awaited before HOST_READY / stream start)
 * - Execute onExitMacro when all players leave with no restart (awaited before pool re-entry)
 * - Execute onInactiveMacro on a 30s interval while no players are connected
 * - Parse macro strings and send appropriate IPC messages to Playjector
 *
 * Macro Format:
 * - `key=<keyCode>@<timing>^<hold>` - Press a keyboard key
 * - `string=<text>@<timing>` - Type a string character-by-character
 * - `gpad=<button>@<timing>^<hold>` - Press a gamepad button (named: A, B, X, Y, DUP, LB, RT, etc.)
 *
 * Modifiers (optional):
 * - `@<ms>` - Delay before executing the step (default: 100ms)
 * - `^<ms>` - How long to hold the key/button (default: 20ms)
 *
 * Examples:
 * - `['key=121', 'string=LoadMap(SOH_LDCoop_Prototype)@1000', 'key=13@200^20']`
 * - `['gpad=A@100^20']` - Press gamepad A button
 */

import type { PavilionCoordinator } from '../../pavilion/PavilionCoordinator';
import { generators } from '@playcastdotio/Messaging';
import { ipcSend } from '../../../api/messaging/protocols/ipc';
import type { PlaycastGamepadXInput, PlaycastKeyDetail, PlaycastTargetButton, XInput } from '@playcastdotio/CoreTypes';
import * as keycode from 'keyboard-key';
import type { IHostAnalyticsManager } from '../../../services/analytics/IHostAnalyticsManager';
import { parseMacroStep, type ParsedStep, type ParsedMouseStep, type ParsedTouchStep, type ParsedMacroStep } from './MacroStepParser';

// ========================================
// Macro Parsing (delegated to MacroStepParser)
// ========================================

// Parsing logic is now in MacroStepParser.ts; we re-use the
// exported parseMacroStep function and types from there.
// Legacy type alias kept for internal usage in this file.
type ParsedLegacyStep = ParsedMacroStep;

// ========================================
// Input Helpers
// ========================================

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function createKeyDetail(keyCode: number, shift: boolean): PlaycastKeyDetail {
  return {
    keyCode,
    location: 'standard',
    source: { type: 'keyboard', code: keyCode },
    modifiers: { ctrl: false, alt: false, shift, meta: false },
    displayName: 'host',
  };
}

function sendKeyDown(keyCode: number, shift: boolean): void {
  ipcSend(generators.keyboard.down('host', createKeyDetail(keyCode, shift)));
}

function sendKeyUp(keyCode: number, shift: boolean): void {
  ipcSend(generators.keyboard.up('host', createKeyDetail(keyCode, shift)));
}

async function sendKeyPress(keyCode: number, shift: boolean, hold: number): Promise<void> {
  sendKeyDown(keyCode, shift);
  await delay(hold);
  sendKeyUp(keyCode, shift);
  await delay(20);
}

// ========================================
// String Typing
// ========================================

const SHIFT_REQUIRED_CHARS = new Set([
  ')', '!', '@', '#', '$', '%', '^', '&', '*', '(',
  '_', '+', '{', '}', ':', '"', '<', '>', '?', '|',
]);

async function sendKeyString(text: string): Promise<void> {
  for (const char of text) {
    const code = keycode.getCode(char);
    if (!code) continue;

    const shift =
      (char === char.toUpperCase() && char !== char.toLowerCase()) ||
      SHIFT_REQUIRED_CHARS.has(char);

    if (shift) {
      sendKeyDown(16, shift);
      await delay(20);
    }

    await sendKeyPress(code, shift, 20);

    if (shift) {
      sendKeyUp(16, shift);
      await delay(20);
    }
  }
}

// ========================================
// Gamepad (XInput)
// ========================================

function toXInputButtonMask(button: string): number {
  switch (button) {
    case 'A': return 0x1000;
    case 'B': return 0x2000;
    case 'X': return 0x4000;
    case 'Y': return 0x8000;
    case 'DUP': return 0x0001;
    case 'DDOWN': return 0x0002;
    case 'DLEFT': return 0x0004;
    case 'DRIGHT': return 0x0008;
    case 'START': return 0x0010;
    case 'BACK': return 0x0020;
    case 'LS': return 0x0040;
    case 'RS': return 0x0080;
    case 'LB': return 0x0100;
    case 'RB': return 0x0200;
    case 'GUIDE': return 0x0400;
    default: return 0;
  }
}

function neutralXInput(): XInput {
  return {
    wButtons: 0,
    bLeftTrigger: 0,
    bRightTrigger: 0,
    sThumbLX: 0,
    sThumbLY: 0,
    sThumbRX: 0,
    sThumbRY: 0,
  };
}

function applyButtonToXInput(button: string, x: XInput): XInput {
  const mask = toXInputButtonMask(button);
  if (mask) return { ...x, wButtons: x.wButtons | mask };

  switch (button) {
    case 'LT': return { ...x, bLeftTrigger: 255 };
    case 'RT': return { ...x, bRightTrigger: 255 };
    default: return x;
  }
}

async function sendGamepadTap(button: string, hold: number): Promise<void> {
  const downX = applyButtonToXInput(button, neutralXInput());

  const base: Omit<PlaycastGamepadXInput, 'xInput'> = {
    gamepadId: 0,
    playerCoordinates: {
      origin: 'bottomLeft',
      dimensions: { x: 0, y: 0 },
      display: { x: 0, y: 0 },
    },
  };

  ipcSend(generators.gamepad.setXInput('host', { ...base, xInput: downX }));
  await delay(hold);

  ipcSend(generators.gamepad.setXInput('host', { ...base, xInput: neutralXInput() }));
  await delay(20);
}

// ========================================
// Mouse (grammar v2)
// ========================================

const MOUSE_BUTTON_LOOKUP: PlaycastTargetButton[] = ['left', 'middle', 'right', 'back', 'forward'];

function numericButtonToTarget(button: number): PlaycastTargetButton {
  return MOUSE_BUTTON_LOOKUP[button] ?? 'left';
}

const ZERO_VEC = { x: 0, y: 0 };
const DEFAULT_PLAYER_COORDS = {
  origin: 'bottomLeft' as const,
  dimensions: ZERO_VEC,
  display: ZERO_VEC,
};

async function sendMouseAction(step: ParsedMouseStep): Promise<void> {
  switch (step.action) {
    case 'move':
      ipcSend(generators.mouse.move('host', {
        playerCoordinates: DEFAULT_PLAYER_COORDS,
        position: ZERO_VEC,
        delta: { x: step.params.dx ?? 0, y: step.params.dy ?? 0 },
      }));
      break;
    case 'down':
      ipcSend(generators.mouse.down('host', {
        playerCoordinates: DEFAULT_PLAYER_COORDS,
        position: ZERO_VEC,
        delta: ZERO_VEC,
        button: numericButtonToTarget(step.params.button ?? 0),
        clickCount: 1,
      }));
      break;
    case 'up':
      ipcSend(generators.mouse.up('host', {
        playerCoordinates: DEFAULT_PLAYER_COORDS,
        position: ZERO_VEC,
        delta: ZERO_VEC,
        button: numericButtonToTarget(step.params.button ?? 0),
        clickCount: 0,
      }));
      break;
    case 'wheel':
      ipcSend(generators.mouse.wheel('host', {
        playerCoordinates: DEFAULT_PLAYER_COORDS,
        position: ZERO_VEC,
        scroll: { x: 0, y: step.params.deltaY ?? 120 },
        mode: 0,
      }));
      break;
  }
  await delay(20);
}

// ========================================
// Touch (grammar v2)
// ========================================

async function sendTouchAction(step: ParsedTouchStep): Promise<void> {
  const touchData = {
    changedTouches: [
      {
        identifier: 0,
        screenX: 0,
        screenY: 0,
        relativeX: step.x,
        relativeY: step.y,
        force: step.action === 'up' ? 0 : 1,
      },
    ],
    altKey: false,
    ctrlKey: false,
    metaKey: false,
    shiftKey: false,
  };

  switch (step.action) {
    case 'down':
      ipcSend(generators.touch.start('host', touchData));
      break;
    case 'move':
      ipcSend(generators.touch.move('host', touchData));
      break;
    case 'up':
      ipcSend(generators.touch.end('host', touchData));
      break;
  }
  await delay(20);
}

// ========================================
// MacroManager Class
// ========================================

export class MacroManager {
  private static readonly INACTIVE_PERIOD_MS = 30_000;
  private static readonly MAX_JOIN_MACRO_WAIT_MS = 4_000;

  private pavilionCoordinator: PavilionCoordinator;
  private analytics: IHostAnalyticsManager;
  private hasExecutedLaunchMacro = false;
  // Incremented on reset() so an in-flight onLaunchMacro from a closing game
  // cannot mark itself executed after reset() and block the next launch.
  private launchMacroGeneration = 0;
  private inactiveIntervalId: ReturnType<typeof setInterval> | null = null;

  constructor(pavilionCoordinator: PavilionCoordinator, analytics: IHostAnalyticsManager) {
    this.pavilionCoordinator = pavilionCoordinator;
    this.analytics = analytics;
  }

  // ========================================
  // Public API
  // ========================================

  /**
   * Execute onLaunchMacro (awaited before host becomes matchmakeable)
   */
  public async executeOnLaunchMacro(): Promise<void> {
    if (this.hasExecutedLaunchMacro) {
      console.log('[MacroManager] onLaunchMacro already executed, skipping');
      return;
    }

    const config = this.pavilionCoordinator.getCampaignConfig();
    if (!config) {
      console.log('[MacroManager] No campaign config available, skipping onLaunchMacro');
      return;
    }

    const { onLaunchMacro } = config;
    if (!onLaunchMacro || onLaunchMacro.length === 0) {
      console.log('[MacroManager] No onLaunchMacro defined for campaign:', config.campaignId);
      return;
    }

    console.log(`[MacroManager] Executing onLaunchMacro for "${config.displayName}":`, onLaunchMacro);

    const generationAtStart = this.launchMacroGeneration;
    let success = false;
    try {
      await this.runMacroSteps(onLaunchMacro, 'Launch');
      if (generationAtStart !== this.launchMacroGeneration) {
        console.log('[MacroManager] onLaunchMacro finished after game closed — not marking as executed');
        return;
      }
      this.hasExecutedLaunchMacro = true;
      success = true;
      console.log('[MacroManager] onLaunchMacro completed successfully');
    } catch (error) {
      console.error('[MacroManager] onLaunchMacro failed:', error);
    } finally {
      this.analytics.sendMacroExecutedEvent({
        macroType: 'onLaunch',
        success,
        campaignId: config.campaignId,
      });
    }
  }

  /**
   * Execute onJoinMacro (awaited before HOST_READY / stream start).
   *
   * Stops the inactive loop first, then races the macro against a timeout cap.
   * If the macro takes longer than MAX_JOIN_MACRO_WAIT_MS, the promise resolves
   * early but the macro continues running in the background.
   */
  public async executeOnJoinMacro(): Promise<void> {
    this.stopInactiveLoop();

    const config = this.pavilionCoordinator.getCampaignConfig();
    if (!config) {
      console.log('[MacroManager] No campaign config available, skipping onJoinMacro');
      return;
    }

    const { onJoinMacro } = config;
    if (!onJoinMacro || onJoinMacro.length === 0) {
      console.log('[MacroManager] No onJoinMacro defined for campaign:', config.campaignId);
      return;
    }

    console.log(`[MacroManager] Executing onJoinMacro for "${config.displayName}":`, onJoinMacro);

    const startTime = Date.now();
    let success = false;
    const macroPromise = this.runMacroSteps(onJoinMacro, 'Player join')
      .then(() => {
        success = true;
        console.log('[MacroManager] onJoinMacro completed successfully');
      })
      .catch((error) => console.error('[MacroManager] onJoinMacro failed:', error))
      .finally(() => {
        this.analytics.sendMacroExecutedEvent({
          macroType: 'onJoin',
          success,
          campaignId: config.campaignId,
        });
      });

    const timeoutPromise = new Promise<void>((resolve) => {
      setTimeout(() => {
        console.warn(`[MacroManager] Player join macro still running after ${MacroManager.MAX_JOIN_MACRO_WAIT_MS}ms, proceeding without waiting.`);
        resolve();
      }, MacroManager.MAX_JOIN_MACRO_WAIT_MS);
    });

    await Promise.race([macroPromise, timeoutPromise]);
  }

  /**
   * Execute onExitMacro (awaited before re-entering matchmaking pool).
   *
   * Called when all players leave and restartOnDisconnect is false.
   * After the macro completes, starts the inactive loop.
   */
  public async executeOnExitMacro(): Promise<void> {
    const config = this.pavilionCoordinator.getCampaignConfig();
    if (!config) {
      console.log('[MacroManager] No campaign config available, skipping onExitMacro');
      this.startInactiveLoop();
      return;
    }

    const { onExitMacro } = config;
    if (!onExitMacro || onExitMacro.length === 0) {
      console.log('[MacroManager] No onExitMacro defined for campaign:', config.campaignId);
      this.startInactiveLoop();
      return;
    }

    console.log(`[MacroManager] Executing onExitMacro for "${config.displayName}":`, onExitMacro);

    const startTime = Date.now();
    let success = false;
    try {
      await this.runMacroSteps(onExitMacro, 'Player Exit');
      success = true;
      console.log('[MacroManager] onExitMacro completed successfully');
    } catch (error) {
      console.error('[MacroManager] onExitMacro failed:', error);
    } finally {
      this.analytics.sendMacroExecutedEvent({
        macroType: 'onExit',
        success,
        campaignId: config.campaignId,
      });
    }

    this.startInactiveLoop();
  }

  /**
   * Execute onInactiveMacro (fire-and-forget from interval timer)
   */
  public async executeOnInactiveMacro(): Promise<void> {
    const config = this.pavilionCoordinator.getCampaignConfig();
    if (!config) return;

    const { onInactiveMacro } = config;
    if (!onInactiveMacro || onInactiveMacro.length === 0) return;

    const startTime = Date.now();
    let success = false;
    try {
      await this.runMacroSteps(onInactiveMacro, 'Inactive');
      success = true;
    } catch (error) {
      console.error('[MacroManager] onInactiveMacro failed:', error);
    } finally {
      this.analytics.sendMacroExecutedEvent({
        macroType: 'onInactive',
        success,
        campaignId: config.campaignId,
      });
    }
  }

  /**
   * Stop the inactive macro loop.
   */
  public stopInactiveLoop(): void {
    if (this.inactiveIntervalId) {
      clearInterval(this.inactiveIntervalId);
      this.inactiveIntervalId = null;
      console.log('[MacroManager] Stopped inactive loop');
    }
  }

  /**
   * Reset launch macro tracking and stop inactive loop (called when game closes)
   */
  public reset(): void {
    this.launchMacroGeneration++;
    this.hasExecutedLaunchMacro = false;
    this.stopInactiveLoop();
    console.log('[MacroManager] Reset launch macro tracking');
  }

  // ========================================
  // Private
  // ========================================

  /**
   * Start the inactive macro loop (fires every 30s).
   */
  private startInactiveLoop(): void {
    this.stopInactiveLoop();

    this.inactiveIntervalId = setInterval(() => {
      void this.executeOnInactiveMacro();
    }, MacroManager.INACTIVE_PERIOD_MS);

    console.log('[MacroManager] Started inactive loop (every 30s)');
  }

  /**
   * Run an array of macro step strings sequentially.
   *
   * Public so the StatefulMacroCoordinator can drive per-node macro execution
   * directly, identically to how `executeOnLaunchMacro` etc. drive it
   * internally. The `label` parameter is purely for log labelling and has a
   * neutral default for stateful-macro callers who use their own logging.
   */
  public async runMacroSteps(steps: string[], label = 'Macro'): Promise<void> {
    if (!steps || steps.length === 0) {
      console.log(`[MacroManager] No steps for ${label}, skipping.`);
      return;
    }

    try {
      ipcSend(
        generators.input.toggleFilters('host', {
          keyboard: false,
          mouse: false,
          gamepad: false,
          touch: false,
        }),
      );
      await delay(100);
  
      console.log(`[MacroManager] Running ${label} with ${steps.length} steps.`);
  
      for (const rawStep of steps) {
        const step = parseMacroStep(rawStep);
        if (!step) continue;

        if (step.type === 'key') {
          const legacyStep = step as ParsedLegacyStep;
          const keyCode = Number(legacyStep.value);
          if (Number.isNaN(keyCode)) {
            console.warn('[MacroManager] Invalid key code in step:', rawStep);
            continue;
          }
  
          console.log(`[MacroManager] Sending key press: ${keyCode}`);
          await delay(legacyStep.timing);
          await sendKeyPress(keyCode, false, legacyStep.hold);
        } else if (step.type === 'string') {
          const legacyStep = step as ParsedLegacyStep;
          console.log(`[MacroManager] Sending string: ${legacyStep.value}`);
          await delay(legacyStep.timing);
          await sendKeyString(legacyStep.value);
        } else if (step.type === 'gpad') {
          const legacyStep = step as ParsedLegacyStep;
          console.log(`[MacroManager] Sending gpad: ${legacyStep.value}`);
          await delay(legacyStep.timing);
          await sendGamepadTap(legacyStep.value, legacyStep.hold);
        } else if (step.type === 'mouse') {
          const mouseStep = step as ParsedMouseStep;
          await delay(mouseStep.timing);
          await sendMouseAction(mouseStep);
        } else if (step.type === 'touch') {
          const touchStep = step as ParsedTouchStep;
          await delay(touchStep.timing);
          await sendTouchAction(touchStep);
        }
      }
    } catch (error) {
      console.error('[MacroManager] Failed to run macro steps:', error);
    } finally {
      ipcSend(
        generators.input.toggleFilters('host', {
          keyboard: true,
          mouse: true,
          gamepad: true,
          touch: true,
          exitGuestModeAfterAdminSession: false,
        }),
      );
    }

    console.log(`[MacroManager] ${label} macro complete.`);
  }
}
